import { useEffect, useState } from 'react';
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SGSButton, SGSFormDate } from 'components';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import SGSTablePagination from 'components/SGSTablePagination';
import { exportPrestationLidl, getHistoryExportAG2R } from 'core/services/affilie';
import dayjs from 'dayjs';
import moment from 'moment';
import { useCustomStyles } from 'core/mui/customStyles';

const schema = z.object({
    startPeriod: z.date({ invalid_type_error: 'Veuillez sélectionner une date de début.' }).transform((val) => (val ? dayjs(val).format('YYYY-MM-DD') : null)),
    endPeriod: z.date({ invalid_type_error: 'Veuillez sélectionner une date de fin.' }).transform((val) => (val ? dayjs(val).format('YYYY-MM-DD') : null)),
});

const SearchAndTable = ({ setIsModalOpen }) => {
    const classes = useCustomStyles();
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalResult, setTotalResult] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [downloadingPrestaLidl, setDownloadingPrestaLidl] = useState(false);

    const methods = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            startPeriod: null,
            endPeriod: null,
        },
    });

    const { handleSubmit, trigger, setValue } = methods;

    const getList = async () => {
        setLoading(true);
        try {
            const result = await getHistoryExportAG2R({
                page: page + 1,
                limit: rowsPerPage,
                sort: { column: 'id', order: 'desc' },
                search: {},
            });
            setRows(result.data);
            setTotalResult(result.totalResultat);
            if (!initialized) {
                const lastExport = result.data[0];
                const start = moment(lastExport?.endPeriod).add(1, 'day').toDate();
                const today = moment().startOf('day').toDate();

                if (start <= today) {
                    setValue('startPeriod', start);
                }
                setInitialized(true);
            }
        } catch (error) {
            console.error('Error fetching export history:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList();
    }, [page, rowsPerPage]);

    const onSubmit = (form) => {
        const isValid = trigger();
        if (!isValid) return;

        setDownloadingPrestaLidl(true);
        exportPrestationLidl({ startPeriod: form.startPeriod, endPeriod: form.endPeriod })
            .then((data) => {
                const url = window.URL.createObjectURL(
                    new Blob([data as BlobPart], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    }),
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `prestations.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .finally(() => {
                setDownloadingPrestaLidl(false);
                setIsModalOpen(false);
            });
    };

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const tableHeight = 60 * 5 + 56; // Define a fixed table height

    return (
        <Box>
            {/* Search Form */}
            <FormProvider {...methods}>
                <Box component={'form'} onSubmit={handleSubmit(onSubmit)}>
                    <Paper elevation={3} sx={{ padding: 2, marginBottom: 3 }}>
                        <Grid container spacing={2} alignItems="flex-start">
                            {/* Start Period Field */}
                            <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '90px' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <SGSFormDate name="startPeriod" label="Du" maxDate={dayjs()} />
                                </LocalizationProvider>
                            </Grid>

                            {/* End Period Field */}
                            <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: '90px' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <SGSFormDate name="endPeriod" label="Au" maxDate={dayjs()} />
                                </LocalizationProvider>
                            </Grid>

                            {/* Export Button */}
                            {/* Export Button */}
                            <Grid item xs={12} sm={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '90px' }}>
                                <Box display="flex" justifyContent="flex-end">
                                    <SGSButton variant="contained" color="primary" type="submit" loading={downloadingPrestaLidl} icon="far fa-file-excel fa-lg">
                                        EXPORTER
                                    </SGSButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            </FormProvider>

            {/* Table */}
            <Paper elevation={3}>
                <TableContainer sx={{ minHeight: tableHeight, maxHeight: tableHeight }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <span className={classes.txtBlackBold}>Utilisateur</span>
                                </TableCell>
                                <TableCell width={250}>
                                    <span className={classes.txtBlackBold}>Période (Début - Fin)</span>
                                </TableCell>
                                <TableCell width={200}>
                                    <span className={classes.txtBlackBold}>Date d'exportation</span>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length > 0 ? (
                                rows.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <span className={classes.txtBlack}>{row.createdBy}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={classes.txtBlack}>
                                                {moment(row.startPeriod).format('DD/MM/YY')} - {moment(row.endPeriod).format('DD/MM/YY')}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={classes.txtBlack}>{moment(row.createdAt).format('DD/MM/YY à H:mm')}</span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        Aucun résultat
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <SGSTablePagination count={totalResult} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} color={'#2574A9'} />
            </Paper>
        </Box>
    );
};

export default SearchAndTable;
